import React, { useState, useRef, useEffect, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import "./PDFViewer.css";

// Worker is configured globally in index.js - no need to set it here

const PDFViewer = React.forwardRef(
  (
    {
      pdfUrl,
      fields = [],
      onFieldAdd,
      onFieldMove,
      onFieldSelect,
      selectedField,
      recipients = [],
      selectedRecipient,
      onNumPagesChange,
      onCurrentPageChange,
      onPageCanvasesChange,
      showPageNav = true,
    },
    ref
  ) => {
    const [scale] = useState(1.5);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [rendering, setRendering] = useState(false);
    const [pageCanvases, setPageCanvases] = useState([]);
    const containerRef = useRef(null);
    const canvasRefs = useRef({});
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [draggedField, setDraggedField] = useState(null);
    const [dragOffset, setDragOffset] = useState(null);
    const [dragPageNumber, setDragPageNumber] = useState(null);
    const [resizingField, setResizingField] = useState(null);
    const [resizeHandle, setResizeHandle] = useState(null);
    const [resizeStart, setResizeStart] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [displayScales, setDisplayScales] = useState({});
    const [editingField, setEditingField] = useState(null);
    const [editingText, setEditingText] = useState("");

    // Load PDF document
    useEffect(() => {
      if (!pdfUrl) {
        return;
      }

      const loadPdf = async () => {
        try {
          const loadingTask = pdfjsLib.getDocument({
            url: pdfUrl,
            cMapUrl: `${window.location.protocol}//${window.location.host}${process.env.PUBLIC_URL || ""}/cmaps/`,
            cMapPacked: true,
          });

          // loadingTask.onProgress = progress => {
          //   console.log(
          //     "PDFViewer: Loading progress:",
          //     progress.loaded,
          //     "/",
          //     progress.total
          //   );
          // };

          const pdf = await loadingTask.promise;

          setPdfDoc(pdf);

          // Notify parent component of page count
          if (onNumPagesChange) {
            onNumPagesChange(pdf.numPages);
          }
        } catch (error) {
          alert("Failed to load PDF document.");
        }
      };

      loadPdf();

      return () => {
        if (pdfDoc) {
          pdfDoc.destroy();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfUrl, onNumPagesChange]);

    // Render all pages
    const renderAllPages = useCallback(async () => {
      if (!pdfDoc || rendering) return;

      setRendering(true);
      const canvases = [];

      try {
        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
          const page = await pdfDoc.getPage(pageNum);
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport: viewport,
          }).promise;

          canvases.push({
            pageNumber: pageNum,
            canvas,
            width: viewport.width,
            height: viewport.height,
          });
        }

        setPageCanvases(canvases);
      } catch (error) {
        // Error rendering PDF pages
      } finally {
        setRendering(false);
      }
    }, [pdfDoc, scale, rendering]);

    // Re-render when PDF or scale changes
    useEffect(() => {
      if (pdfDoc) {
        renderAllPages();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfDoc, scale]);

    // Notify parent when page canvases change
    useEffect(() => {
      if (onPageCanvasesChange && pageCanvases.length > 0) {
        onPageCanvasesChange(pageCanvases);
      }
    }, [pageCanvases, onPageCanvasesChange]);

    // Calculate display scale for each page (responsive to window resizing)
    const calculateDisplayScales = useCallback(() => {
      const scales = {};
      pageCanvases.forEach(pageCanvas => {
        const canvasElement = canvasRefs.current[pageCanvas.pageNumber];
        if (canvasElement) {
          const rect = canvasElement.getBoundingClientRect();
          // Display scale = how the canvas is displayed vs its internal size
          const displayScale = rect.width / pageCanvas.width;
          scales[pageCanvas.pageNumber] = displayScale;
        }
      });
      setDisplayScales(scales);
    }, [pageCanvases]);

    // Calculate display scales when canvases are ready or window resizes
    useEffect(() => {
      if (pageCanvases.length > 0) {
        // Delay initial calculation to ensure canvas refs are populated
        const timer = setTimeout(() => {
          calculateDisplayScales();
        }, 100);

        // Recalculate on window resize
        const handleResize = () => {
          calculateDisplayScales();
        };

        window.addEventListener("resize", handleResize);
        return () => {
          clearTimeout(timer);
          window.removeEventListener("resize", handleResize);
        };
      }
    }, [pageCanvases, calculateDisplayScales]);

    // Get the display scale for a specific page
    // Calculate on-demand from canvas element if displayScales not ready
    const getDisplayScale = useCallback(
      pageNumber => {
        // Try to get from cached displayScales first
        if (displayScales[pageNumber]) {
          return displayScales[pageNumber];
        }

        // If not available, calculate directly from canvas element
        const canvasElement = canvasRefs.current[pageNumber];
        const pageCanvas = pageCanvases.find(p => p.pageNumber === pageNumber);

        if (canvasElement && pageCanvas) {
          const rect = canvasElement.getBoundingClientRect();
          return rect.width / pageCanvas.width;
        }

        // Fallback to 1 if nothing is available
        return 1;
      },
      [displayScales, pageCanvases]
    );

    const getPageFromMouseEvent = e => {
      const container = containerRef.current;
      if (!container) return null;

      const containerRect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const relativeY = e.clientY - containerRect.top + scrollTop;

      let cumulativeHeight = 20; // Initial padding
      for (const pageCanvas of pageCanvases) {
        const pageHeight = pageCanvas.height + 20; // 20px gap between pages
        if (relativeY < cumulativeHeight + pageHeight) {
          return {
            pageNumber: pageCanvas.pageNumber,
            offsetY: relativeY - cumulativeHeight,
            canvasElement: canvasRefs.current[pageCanvas.pageNumber],
            scrollTop: scrollTop, // Include scroll position for accurate calculations
          };
        }
        cumulativeHeight += pageHeight;
      }
      return null;
    };

    const handleResizeMouseDown = (e, field, handle) => {
      e.stopPropagation();
      setResizingField(field);
      setResizeHandle(handle);
      setResizeStart({
        x: field.x_position,
        y: field.y_position,
        width: field.width,
        height: field.height,
        mouseX: e.clientX,
        mouseY: e.clientY,
      });
    };

    const handleMouseDown = e => {
      if (!onFieldAdd || e.target.classList.contains("field-box")) return;

      const pageInfo = getPageFromMouseEvent(e);
      if (!pageInfo || !pageInfo.canvasElement) return;

      const rect = pageInfo.canvasElement.getBoundingClientRect();

      // Calculate position relative to the canvas, accounting for container offset
      // Use actual displayed canvas width to get the real display scale
      const pageCanvas = pageCanvases.find(
        p => p.pageNumber === pageInfo.pageNumber
      );
      const displayScale = pageCanvas ? rect.width / pageCanvas.width : 1;
      const x = (e.clientX - rect.left) / displayScale;
      const y = (e.clientY - rect.top) / displayScale;

      setIsDragging(true);
      setDragStart({ x, y });
      setDragPageNumber(pageInfo.pageNumber);
    };

    const handleFieldMouseDown = (e, field) => {
      e.stopPropagation();

      // Ignore if clicking on a resize handle or if editing
      if (e.target.classList.contains("resize-handle") || editingField) {
        return;
      }

      setDraggedField(field);
      const pageInfo = getPageFromMouseEvent(e);
      if (!pageInfo || !pageInfo.canvasElement) return;

      const rect = pageInfo.canvasElement.getBoundingClientRect();
      // Get the actual display scale for accurate positioning
      const pageCanvas = pageCanvases.find(
        p => p.pageNumber === pageInfo.pageNumber
      );
      const displayScale = pageCanvas ? rect.width / pageCanvas.width : 1;
      const mouseX = (e.clientX - rect.left) / displayScale;
      const mouseY = (e.clientY - rect.top) / displayScale;

      setDragOffset({
        x: mouseX - field.x_position,
        y: mouseY - field.y_position,
      });
    };

    const handleFieldDoubleClick = (e, field) => {
      e.stopPropagation();

      // Only allow editing for text-based fields
      if (
        field.field_type === "text" ||
        field.field_type === "email" ||
        field.field_type === "phone"
      ) {
        setEditingField(field.id);
        setEditingText(field.default_value || "");
      }
    };

    const handleFieldTextChange = e => {
      setEditingText(e.target.value);
    };

    const handleFieldTextBlur = () => {
      if (editingField && onFieldMove) {
        // Only update the default_value property
        onFieldMove(editingField, {
          default_value: editingText,
        });
      }
      setEditingField(null);
      setEditingText("");
    };

    const handleFieldTextKeyDown = e => {
      // Allow Enter for new lines in textarea
      if (e.key === "Enter" && e.shiftKey) {
        // Let the default behavior happen (new line)
        return;
      } else if (e.key === "Enter") {
        // Enter without shift saves
        e.preventDefault();
        handleFieldTextBlur();
      } else if (e.key === "Escape") {
        setEditingField(null);
        setEditingText("");
      }
    };

    const handleMouseMove = e => {
      const pageInfo = getPageFromMouseEvent(e);
      if (!pageInfo || !pageInfo.canvasElement) return;

      const rect = pageInfo.canvasElement.getBoundingClientRect();
      // Get the actual display scale for accurate positioning
      const pageCanvas = pageCanvases.find(
        p => p.pageNumber === pageInfo.pageNumber
      );
      const displayScale = pageCanvas ? rect.width / pageCanvas.width : 1;
      const mouseX = (e.clientX - rect.left) / displayScale;
      const mouseY = (e.clientY - rect.top) / displayScale;

      // Handle field resizing
      if (resizingField && resizeStart && resizeHandle) {
        const deltaX = (e.clientX - resizeStart.mouseX) / displayScale;
        const deltaY = (e.clientY - resizeStart.mouseY) / displayScale;

        let newX = resizeStart.x;
        let newY = resizeStart.y;
        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;

        // Minimum field dimensions
        const minWidth = 20;
        const minHeight = 20;

        // Calculate new dimensions based on which handle is being dragged
        switch (resizeHandle) {
          case "se": // bottom-right
            newWidth = Math.max(minWidth, resizeStart.width + deltaX);
            newHeight = Math.max(minHeight, resizeStart.height + deltaY);
            break;
          case "sw": // bottom-left
            newWidth = Math.max(minWidth, resizeStart.width - deltaX);
            newHeight = Math.max(minHeight, resizeStart.height + deltaY);
            newX = resizeStart.x + resizeStart.width - newWidth;
            break;
          case "ne": // top-right
            newWidth = Math.max(minWidth, resizeStart.width + deltaX);
            newHeight = Math.max(minHeight, resizeStart.height - deltaY);
            newY = resizeStart.y + resizeStart.height - newHeight;
            break;
          case "nw": // top-left
            newWidth = Math.max(minWidth, resizeStart.width - deltaX);
            newHeight = Math.max(minHeight, resizeStart.height - deltaY);
            newX = resizeStart.x + resizeStart.width - newWidth;
            newY = resizeStart.y + resizeStart.height - newHeight;
            break;
          default:
            break;
        }

        // Update field position and dimensions
        if (onFieldMove) {
          onFieldMove(resizingField.id, {
            x_position: newX,
            y_position: newY,
            width: newWidth,
            height: newHeight,
          });
        }
      }
      // Handle field dragging
      else if (draggedField && dragOffset) {
        const newX = mouseX - dragOffset.x;
        const newY = mouseY - dragOffset.y;

        // Check if the field is being moved to a different page
        const updateData = {
          x_position: newX,
          y_position: newY,
        };

        // If the mouse is over a different page than the field's current page, update the page number
        if (pageInfo.pageNumber !== draggedField.page_number) {
          updateData.page_number = pageInfo.pageNumber;
        }

        onFieldMove(draggedField.id, updateData);
      }
      // Handle new field creation
      else if (isDragging && dragStart) {
        const dragPreviewColor = getDragPreviewColor();

        // If we've moved to a different page, hide the old preview and update the drag page
        if (dragPageNumber && pageInfo.pageNumber !== dragPageNumber) {
          const oldPreview = document.getElementById(
            `drag-preview-${dragPageNumber}`
          );
          if (oldPreview) {
            oldPreview.style.display = "none";
          }
          // Reset drag start position for the new page
          setDragPageNumber(pageInfo.pageNumber);
          setDragStart({ x: mouseX, y: mouseY });
        }

        // Show preview on the current page
        const preview = document.getElementById(
          `drag-preview-${pageInfo.pageNumber}`
        );
        if (preview) {
          preview.style.display = "block";
          preview.style.left = `${Math.min(dragStart.x, mouseX) * displayScale}px`;
          preview.style.top = `${Math.min(dragStart.y, mouseY) * displayScale}px`;
          preview.style.width = `${Math.abs(mouseX - dragStart.x) * displayScale}px`;
          preview.style.height = `${Math.abs(mouseY - dragStart.y) * displayScale}px`;
          preview.style.borderColor = dragPreviewColor;
          preview.style.backgroundColor = hexToRgba(dragPreviewColor, 0.1);
        }
      }
    };

    const handleMouseUp = e => {
      // Clear resize state
      if (resizingField) {
        setResizingField(null);
        setResizeHandle(null);
        setResizeStart(null);
        return;
      }

      // Clear field dragging state
      if (draggedField) {
        setDraggedField(null);
        setDragOffset(null);
        return; // Exit early if we were just dragging a field
      }

      // Handle new field creation
      if (isDragging && dragStart && onFieldAdd) {
        // Immediately clear drag state to prevent duplicate creation
        const currentDragStart = dragStart;
        const currentPageNumber = dragPageNumber;

        setIsDragging(false);
        setDragStart(null);
        setDragPageNumber(null);

        // Hide preview immediately on all pages
        if (currentPageNumber) {
          const preview = document.getElementById(
            `drag-preview-${currentPageNumber}`
          );
          if (preview) {
            preview.style.display = "none";
          }
        }

        const pageInfo = getPageFromMouseEvent(e);
        if (!pageInfo || !pageInfo.canvasElement) return;

        // Use same coordinate calculation as in handleMouseDown and handleMouseMove
        const rect = pageInfo.canvasElement.getBoundingClientRect();
        const container = containerRef.current;
        if (!container) return;

        // Calculate mouse position relative to the canvas, accounting for scrolling
        // Get the actual display scale
        const pageCanvas = pageCanvases.find(
          p => p.pageNumber === pageInfo.pageNumber
        );
        const displayScale = pageCanvas ? rect.width / pageCanvas.width : 1;
        const mouseX = (e.clientX - rect.left) / displayScale;
        const mouseY = (e.clientY - rect.top) / displayScale;

        const width = Math.abs(mouseX - currentDragStart.x);
        const height = Math.abs(mouseY - currentDragStart.y);

        // Only create field if drag was significant
        if (width > 5 && height > 5) {
          // Get the page canvas to store original dimensions
          // Use the page where the mouse is released, not the starting page
          const finalPageCanvas = pageCanvases.find(
            p => p.pageNumber === pageInfo.pageNumber
          );

          const field = {
            x_position: Math.min(currentDragStart.x, mouseX),
            y_position: Math.min(currentDragStart.y, mouseY),
            width,
            height,
            page_number: pageInfo.pageNumber, // Use current page, not starting page
            // Store original page dimensions for proper scaling
            page_width: finalPageCanvas ? finalPageCanvas.width / scale : null,
            page_height: finalPageCanvas
              ? finalPageCanvas.height / scale
              : null,
          };
          onFieldAdd(field);
        }
      }
    };

    const getPageOffset = useCallback(
      pageNumber => {
        let offset = 20; // Initial padding
        for (const pageCanvas of pageCanvases) {
          if (pageCanvas.pageNumber === pageNumber) {
            return offset;
          }
          offset += pageCanvas.height + 20; // 20px gap between pages
        }
        return 0;
      },
      [pageCanvases]
    );

    // Get the color for a field based on its assigned recipient ID
    const getFieldColor = field => {
      if (!field.assigned_to_recipient_role || !recipients.length) {
        return "#007bff"; // Default blue
      }

      const recipient = recipients.find(
        r => r.id === field.assigned_to_recipient_role
      );

      return recipient?.color || "#007bff";
    };

    // Convert hex color to rgba with alpha
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Get the current role's color for the drag preview
    const getDragPreviewColor = () => {
      if (selectedRecipient?.color) {
        return selectedRecipient.color;
      }
      return "#007bff";
    };

    // Scroll to specific page
    const scrollToPage = useCallback(
      pageNumber => {
        const container = containerRef.current;
        if (!container) return;

        const pageOffset = getPageOffset(pageNumber);
        container.scrollTop = pageOffset - 20; // Subtract initial padding
        setCurrentPage(pageNumber);
        if (onCurrentPageChange) {
          onCurrentPageChange(pageNumber);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [getPageOffset, onCurrentPageChange]
    );

    // Expose scrollToPage via ref
    React.useImperativeHandle(
      ref,
      () => ({
        scrollToPage,
      }),
      [scrollToPage]
    );

    // Track current page based on scroll position
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleScroll = () => {
        const scrollTop = container.scrollTop;
        let cumulativeHeight = 20;

        for (const pageCanvas of pageCanvases) {
          const pageHeight = pageCanvas.height + 20;
          if (scrollTop < cumulativeHeight + pageHeight / 2) {
            setCurrentPage(pageCanvas.pageNumber);
            if (onCurrentPageChange) {
              onCurrentPageChange(pageCanvas.pageNumber);
            }
            break;
          }
          cumulativeHeight += pageHeight;
        }
      };

      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }, [pageCanvases, onCurrentPageChange]);

    return (
      <div className="pdf-viewer-container">
        <div className="pdf-viewer-with-nav">
          <div
            className="pdf-page-container"
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <div className="pdf-pages-wrapper">
              {pageCanvases.map((pageCanvas, index) => {
                const dragPreviewColor = getDragPreviewColor();
                return (
                  <div
                    key={pageCanvas.pageNumber}
                    className="pdf-page-wrapper"
                    style={{
                      position: "relative",
                      marginBottom:
                        index < pageCanvases.length - 1 ? "20px" : "0",
                    }}
                  >
                    <canvas
                      ref={canvas => {
                        if (canvas && pageCanvas.canvas) {
                          const ctx = canvas.getContext("2d");
                          canvas.width = pageCanvas.canvas.width;
                          canvas.height = pageCanvas.canvas.height;
                          ctx.drawImage(pageCanvas.canvas, 0, 0);
                          canvasRefs.current[pageCanvas.pageNumber] = canvas;
                        }
                      }}
                      style={{
                        display: "block",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      }}
                    />

                    {/* Render field boxes for this page */}
                    {fields
                      .filter(f => f.page_number === pageCanvas.pageNumber)
                      .map(field => {
                        const fieldColor = getFieldColor(field);
                        const isSelected = selectedField?.id === field.id;

                        // Calculate scaled positions using display scale for responsive positioning
                        // Get the display scale for this specific page
                        const fieldDisplayScale = getDisplayScale(
                          pageCanvas.pageNumber
                        );

                        const leftPx = field.x_position * fieldDisplayScale;
                        const topPx = field.y_position * fieldDisplayScale;
                        const widthPx = field.width * fieldDisplayScale;
                        const heightPx = field.height * fieldDisplayScale;

                        const isEditing = editingField === field.id;
                        const isTextType =
                          field.field_type === "text" ||
                          field.field_type === "email" ||
                          field.field_type === "phone";

                        return (
                          <div
                            key={field.id}
                            className={`field-box ${isSelected ? "selected" : ""} ${isEditing ? "editing" : ""}`}
                            onMouseDown={e => handleFieldMouseDown(e, field)}
                            onDoubleClick={e =>
                              handleFieldDoubleClick(e, field)
                            }
                            onClick={() =>
                              onFieldSelect && onFieldSelect(field)
                            }
                            style={{
                              position: "absolute",
                              left: `${leftPx}px`,
                              top: `${topPx}px`,
                              width: `${widthPx}px`,
                              height: `${heightPx}px`,
                              border: `2px solid ${fieldColor}`,
                              backgroundColor: hexToRgba(fieldColor, 0.1),
                              cursor: isEditing ? "text" : "move",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              color: fieldColor,
                              fontWeight: "bold",
                              zIndex: 100,
                              padding: "2px",
                              overflow: "visible",
                            }}
                          >
                            {isEditing ? (
                              <textarea
                                value={editingText}
                                onChange={handleFieldTextChange}
                                onBlur={handleFieldTextBlur}
                                onKeyDown={handleFieldTextKeyDown}
                                autoFocus
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  border: "none",
                                  background: "transparent",
                                  color: "#000",
                                  fontSize: "11px",
                                  fontWeight: "normal",
                                  textAlign: "left",
                                  outline: "none",
                                  padding: "4px",
                                  resize: "none",
                                  overflow: "hidden",
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                              />
                            ) : (
                              <div
                                title={isTextType ? "Double-click to edit" : ""}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  padding: "4px",
                                  overflow: "hidden",
                                  color: field.default_value
                                    ? "#000"
                                    : fieldColor,
                                  fontSize: field.default_value
                                    ? "11px"
                                    : "12px",
                                  fontWeight: field.default_value
                                    ? "normal"
                                    : "bold",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  hyphens: "auto",
                                  whiteSpace: "pre-wrap",
                                  textAlign: "center",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 20,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.3",
                                }}
                              >
                                {field.default_value || field.label}
                              </div>
                            )}

                            {/* Resize handles - only show for selected field */}
                            {isSelected && (
                              <>
                                <div
                                  className="resize-handle resize-handle-nw"
                                  onMouseDown={e =>
                                    handleResizeMouseDown(e, field, "nw")
                                  }
                                  style={{
                                    position: "absolute",
                                    top: "-4px",
                                    left: "-4px",
                                    width: "8px",
                                    height: "8px",
                                    backgroundColor: fieldColor,
                                    border: "1px solid white",
                                    cursor: "nw-resize",
                                    zIndex: 10,
                                  }}
                                />
                                <div
                                  className="resize-handle resize-handle-ne"
                                  onMouseDown={e =>
                                    handleResizeMouseDown(e, field, "ne")
                                  }
                                  style={{
                                    position: "absolute",
                                    top: "-4px",
                                    right: "-4px",
                                    width: "8px",
                                    height: "8px",
                                    backgroundColor: fieldColor,
                                    border: "1px solid white",
                                    cursor: "ne-resize",
                                    zIndex: 10,
                                  }}
                                />
                                <div
                                  className="resize-handle resize-handle-sw"
                                  onMouseDown={e =>
                                    handleResizeMouseDown(e, field, "sw")
                                  }
                                  style={{
                                    position: "absolute",
                                    bottom: "-4px",
                                    left: "-4px",
                                    width: "8px",
                                    height: "8px",
                                    backgroundColor: fieldColor,
                                    border: "1px solid white",
                                    cursor: "sw-resize",
                                    zIndex: 10,
                                  }}
                                />
                                <div
                                  className="resize-handle resize-handle-se"
                                  onMouseDown={e =>
                                    handleResizeMouseDown(e, field, "se")
                                  }
                                  style={{
                                    position: "absolute",
                                    bottom: "-4px",
                                    right: "-4px",
                                    width: "8px",
                                    height: "8px",
                                    backgroundColor: fieldColor,
                                    border: "1px solid white",
                                    cursor: "se-resize",
                                    zIndex: 10,
                                  }}
                                />
                              </>
                            )}
                          </div>
                        );
                      })}

                    {/* Drag preview for this page */}
                    <div
                      id={`drag-preview-${pageCanvas.pageNumber}`}
                      style={{
                        position: "absolute",
                        border: `2px dashed ${dragPreviewColor}`,
                        backgroundColor: hexToRgba(dragPreviewColor, 0.1),
                        pointerEvents: "none",
                        display: "none",
                        zIndex: 5,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Page Navigation Sidebar - only show if showPageNav is true */}
          {showPageNav && (
            <div className="page-navigation-sidebar">
              <div className="page-thumbnails">
                {pageCanvases.map(pageCanvas => (
                  <div
                    key={pageCanvas.pageNumber}
                    className={`page-thumbnail ${currentPage === pageCanvas.pageNumber ? "active" : ""}`}
                    onClick={() => scrollToPage(pageCanvas.pageNumber)}
                  >
                    <div className="thumbnail-wrapper">
                      <canvas
                        ref={canvas => {
                          if (canvas && pageCanvas.canvas) {
                            const ctx = canvas.getContext("2d");
                            const thumbScale = 0.2; // Small thumbnail
                            canvas.width = pageCanvas.canvas.width * thumbScale;
                            canvas.height =
                              pageCanvas.canvas.height * thumbScale;
                            ctx.scale(thumbScale, thumbScale);
                            ctx.drawImage(pageCanvas.canvas, 0, 0);
                          }
                        }}
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                      />
                    </div>
                    <div className="thumbnail-label">
                      Page {pageCanvas.pageNumber}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

PDFViewer.displayName = "PDFViewer";

export default PDFViewer;
